import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { X, FileText, File } from "lucide-react";
import { Spinner } from "@/components/ui/spinner";

// ==================== Types ====================

export interface FilePreviewModalProps {
  // File Information
  fileUrl: string;
  fileName?: string;
  fileType?: string;
  text?: string;
  description?: string;
  // Display Options
  trigger?: React.ReactNode;
  dialogTitle?: string;
  showDownloadButton?: boolean;
  showFileName?: boolean;
  maxWidth?: string;
  useGoogleViewer?: boolean; // Option to use Google PDF Viewer

  // Callbacks
  onDownload?: () => void;
  onOpen?: () => void;
  onClose?: () => void;
}

// ==================== Utility Functions ====================

function getFileTypeFromUrl(url: string, providedType?: string): string {
  if (providedType) return providedType.toLowerCase();

  const extension = url.split(".").pop()?.toLowerCase() || "";
  return extension;
}

function getFileNameFromUrl(url: string, providedName?: string): string {
  if (providedName) return providedName;

  const urlParts = url.split("/");
  return urlParts[urlParts.length - 1] || "document";
}

function isImageFile(fileType: string): boolean {
  const imageTypes = ["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg"];
  return imageTypes.includes(fileType.toLowerCase());
}

function isPdfFile(fileType: string): boolean {
  return fileType.toLowerCase() === "pdf";
}

function isTextFile(fileType: string): boolean {
  const textTypes = ["txt", "csv", "json", "xml", "log", "md"];
  return textTypes.includes(fileType.toLowerCase());
}

function isOfficeFile(fileType: string): boolean {
  const officeTypes = ["doc", "docx", "xls", "xlsx", "ppt", "pptx"];
  return officeTypes.includes(fileType.toLowerCase());
}

// ==================== Main Component ====================

export const FilePreviewModal = ({
  fileUrl,
  fileName: providedFileName,
  fileType: providedFileType,
  trigger,
  dialogTitle = "ເບິ່ງຕົວຢ່າງເອກະສານ",
  // showDownloadButton = true,
  showFileName = true,
  maxWidth = "max-w-5xl",
  text,
  description,
  useGoogleViewer = false,
  // onDownload,
  onOpen,
  onClose,
}: FilePreviewModalProps) => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [textContent, setTextContent] = useState<string>("");

  const fileType = getFileTypeFromUrl(fileUrl, providedFileType);
  const fileName = getFileNameFromUrl(fileUrl, providedFileName);

  // Default trigger button
  const defaultTrigger = (
    <div className="flex flex-col gap-2">
      <label htmlFor="label" className="text-gray-900 text-[14px]">
        ຕົວຢ່າງເອກະສານ
      </label>
      <div className="flex gap-2 items-center bg-[#FAFAFA] p-3 rounded-md border-dashed bottom-1 cursor-pointer">
        <img src="/pdf-icons.png" alt="logo" width={54} height={54} />
        {text ? text : "ເບິ່ງເອກະສານ"}
      </div>
    </div>
  );

  // Handle download
  // const handleDownload = async () => {
  //   if (onDownload) {
  //     onDownload();
  //     return;
  //   }

  //   try {
  //     const response = await fetch(fileUrl);
  //     const blob = await response.blob();
  //     const url = window.URL.createObjectURL(blob);
  //     const link = document.createElement("a");
  //     link.href = url;
  //     link.download = fileName;
  //     document.body.appendChild(link);
  //     link.click();
  //     document.body.removeChild(link);
  //     window.URL.revokeObjectURL(url);
  //   } catch (err) {
  //     console.error("Download error:", err);
  //     setError("ບໍ່ສາມາດດາວໂຫຼດໄຟລ໌ໄດ້");
  //   }
  // };

  // Handle dialog open/close
  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (newOpen && onOpen) {
      onOpen();
      setIsLoading(true);
      setError(null);
      setTextContent("");

      // Load text content for text files
      if (isTextFile(fileType)) {
        loadTextContent();
      }
    } else if (!newOpen && onClose) {
      onClose();
    }
  };

  // Load text file content
  const loadTextContent = async () => {
    try {
      const response = await fetch(fileUrl);
      const text = await response.text();
      setTextContent(text);
      setIsLoading(false);
    } catch (err) {
      console.error("Error loading text file:", err);
      setError("ບໍ່ສາມາດໂຫຼດໄຟລ໌ໄດ້");
      setIsLoading(false);
    }
  };

  // Render file preview based on type
  const renderPreview = () => {
    // PDF Preview
    if (isPdfFile(fileType)) {
      // Use Google Docs Viewer if enabled or as fallback
      const pdfViewerUrl = useGoogleViewer
        ? `https://docs.google.com/viewer?url=${encodeURIComponent(
            fileUrl
          )}&embedded=true`
        : fileUrl;

      return (
        <div className="w-full h-[calc(90vh-200px)] min-h-[500px]">
          {isLoading && (
            <div className="flex items-center justify-center h-full">
              <Spinner className="w-8 h-8" />
              <span className="ml-2">ກຳລັງໂຫຼດເອກະສານ...</span>
            </div>
          )}
          {error && (
            <div className="flex flex-col items-center justify-center h-full text-red-500">
              <FileText className="w-16 h-16 mb-4" />
              <p>{error}</p>
            </div>
          )}
          {useGoogleViewer ? (
            <iframe
              src={pdfViewerUrl}
              className="w-full h-full border-0 rounded"
              title={fileName}
              onLoad={() => setIsLoading(false)}
              onError={() => {
                setIsLoading(false);
                setError("ບໍ່ສາມາດໂຫຼດເອກະສານ PDF ໄດ້");
              }}
            />
          ) : (
            <object
              data={`${pdfViewerUrl}#view=FitH&toolbar=1&navpanes=1`}
              type="application/pdf"
              className="w-full h-full border-0 rounded"
              onLoad={() => setIsLoading(false)}
            >
              <embed
                src={`${pdfViewerUrl}#view=FitH&toolbar=1&navpanes=1`}
                type="application/pdf"
                className="w-full h-full border-0 rounded"
              />
            </object>
          )}
        </div>
      );
    }

    // Image Preview
    if (isImageFile(fileType)) {
      return (
        <div className="flex items-center justify-center w-full min-h-[400px] max-h-[calc(90vh-200px)] bg-gray-50 rounded">
          {isLoading && (
            <div className="flex items-center justify-center">
              <Spinner className="w-8 h-8" />
              <span className="ml-2">ກຳລັງໂຫຼດຮູບພາບ...</span>
            </div>
          )}
          {error && (
            <div className="flex flex-col items-center justify-center text-red-500">
              <File className="w-16 h-16 mb-4" />
              <p>{error}</p>
            </div>
          )}
          <img
            src={fileUrl}
            alt={fileName}
            className={`max-w-full max-h-[calc(90vh-200px)] object-contain rounded ${
              isLoading ? "hidden" : ""
            }`}
            onLoad={() => setIsLoading(false)}
            onError={() => {
              setIsLoading(false);
              setError("ບໍ່ສາມາດໂຫຼດຮູບພາບໄດ້");
            }}
          />
        </div>
      );
    }

    // Text File Preview (TXT, CSV, JSON, etc.)
    if (isTextFile(fileType)) {
      return (
        <div className="w-full min-h-[400px] max-h-[calc(90vh-200px)] overflow-auto">
          {isLoading && (
            <div className="flex items-center justify-center h-full p-12">
              <Spinner className="w-8 h-8" />
              <span className="ml-2">ກຳລັງໂຫຼດໄຟລ໌...</span>
            </div>
          )}
          {error && (
            <div className="flex flex-col items-center justify-center h-full text-red-500 p-12">
              <FileText className="w-16 h-16 mb-4" />
              <p>{error}</p>
            </div>
          )}
          {!isLoading && !error && (
            <div className="p-4 bg-gray-50 rounded">
              <pre className="text-sm whitespace-pre-wrap break-words font-mono">
                {textContent}
              </pre>
            </div>
          )}
        </div>
      );
    }

    // Office Files Preview (DOCX, XLSX, PPTX) - Using Office Online Viewer
    if (isOfficeFile(fileType)) {
      const officeViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
        fileUrl
      )}`;

      return (
        <div className="w-full h-[calc(90vh-200px)] min-h-[500px]">
          {isLoading && (
            <div className="flex items-center justify-center h-full">
              <Spinner className="w-8 h-8" />
              <span className="ml-2">ກຳລັງໂຫຼດເອກະສານ...</span>
            </div>
          )}
          {error && (
            <div className="flex flex-col items-center justify-center h-full text-red-500">
              <FileText className="w-16 h-16 mb-4" />
              <p>{error}</p>
              <p className="text-sm text-gray-500 mt-2">
                ກະລຸນາດາວໂຫຼດເພື່ອເບິ່ງເອກະສານ
              </p>
            </div>
          )}
          <iframe
            src={officeViewerUrl}
            className="w-full h-full border-0 rounded"
            title={fileName}
            onLoad={() => setIsLoading(false)}
            onError={() => {
              setIsLoading(false);
              setError("ບໍ່ສາມາດໂຫຼດເອກະສານໄດ້");
            }}
          />
        </div>
      );
    }

    // Unsupported file type
    return (
      <div className="flex flex-col items-center justify-center p-12 text-center">
        <File className="w-24 h-24 mb-4 text-gray-400" />
        <p className="mb-2 text-lg font-semibold">{fileName}</p>
        <p className="mb-4 text-gray-500">
          ບໍ່ສາມາດເບິ່ງຕົວຢ່າງໄຟລ໌ປະເພດນີ້ໄດ້
        </p>
        <p className="text-sm text-gray-400 mb-4">
          ປະເພດໄຟລ໌: {fileType.toUpperCase()}
        </p>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger || defaultTrigger}</DialogTrigger>
      <DialogContent
        className={`${maxWidth} max-h-[95vh] overflow-hidden flex flex-col`}
      >
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle>{dialogTitle}</DialogTitle>
          </div>
          {showFileName && (
            <>
              <p className="text-sm text-gray-500 mt-1">
                ຊື່ເອກະສານ : <b>{text}</b>
              </p>
            </>
          )}
        </DialogHeader>

        <div className="flex-1 overflow-auto">{renderPreview()}</div>

        {/* Footer Actions */}
        <div className="flex justify-between items-center border-t">
          {description && (
            <p className="text-sm text-gray-500 mt-1">
              ຄຳອະທິບາຍເພິ່ມຕື່ມ : <b>{description}</b>
            </p>
          )}
          <div className="flex justify-end gap-2 pt-4  flex-shrink-0">
            {/* <div className="flex items-center gap-2">
              {showDownloadButton && (
                <Button
                  type="button"
                  variant="default"
                  size="sm"
                  onClick={handleDownload}
                  className="flex items-center gap-x-2"
                >
                  <Download className="w-4 h-4" />
                  ດາວໂຫຼດ
                </Button>
              )}
            </div> */}
            <Button
              type="button"
              variant="destructive"
              onClick={() => setOpen(false)}
            >
              <X className="w-4 h-4 mr-1" />
              ປິດ
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FilePreviewModal;
